import { useEffect, useState } from 'react';
import { KeyRound, Mail, Monitor, User as UserIcon } from 'lucide-react';

import { Button } from '@/components/Button';
import { Collapse } from '@/components/Collapse';
import { Field } from '@/components/Field';
import { Segmented } from '@/components/Segmented';
import { ApiError } from '@/lib/api';
import {
  getThemePreference,
  setThemePreference,
  type ThemePreference,
} from '@/lib/theme';
import { toast } from '@/lib/toast';
import { useChangeEmail, useChangePassword, useCurrentUser, useUpdatePreferences } from './useAuth';

/**
 * Profile: who the account belongs to, and the two credentials that get it in.
 *
 * Name sits in an always-open form because editing it is harmless. Email and
 * password are folded away behind a disclosure -- both require the current
 * password, both are rare, and neither should be one stray keystroke from
 * changing while you are here to fix your timezone.
 */
export function ProfileSettings() {
  return (
    <div className="flex flex-col gap-3">
      <NameCard />
      <AppearanceCard />
      <EmailCard />
      <PasswordCard />
    </div>
  );
}

function CardHeader({
  icon,
  title,
  value,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  value?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
      <span className="text-text-tertiary" aria-hidden>
        {icon}
      </span>
      <div className="mr-auto flex min-w-0 flex-col">
        <h3 className="text-body font-medium text-text">{title}</h3>
        {value && (
          <p className="truncate text-small text-text-secondary">{value}</p>
        )}
      </div>
      {action}
    </div>
  );
}

function NameCard() {
  const { data: user } = useCurrentUser();
  const update = useUpdatePreferences();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');

  useEffect(() => {
    if (!user) return;
    setFirst(user.first_name ?? '');
    setLast(user.last_name ?? '');
  }, [user]);

  const dirty =
    first.trim() !== (user?.first_name ?? '') || last.trim() !== (user?.last_name ?? '');

  return (
    <form
      className="rounded-lg border border-border bg-surface-1"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { first_name: first.trim() || null, last_name: last.trim() || null },
          {
            onSuccess: () => toast.show('Name saved', { tone: 'success' }),
            onError: (err) =>
              toast.error(err instanceof ApiError ? err.message : 'Could not save'),
          },
        );
      }}
    >
      <CardHeader
        icon={<UserIcon size={16} strokeWidth={1.5} />}
        title="Name"
        value="How Chronos addresses you in the app."
      />
      <div className="flex flex-col gap-4 border-t border-border p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="First name"
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            maxLength={80}
            autoComplete="given-name"
          />
          <Field
            label="Last name"
            value={last}
            onChange={(e) => setLast(e.target.value)}
            maxLength={80}
            autoComplete="family-name"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={update.isPending}
            disabled={!dirty}
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

function AppearanceCard() {
  const [theme, setTheme] = useState<ThemePreference>(() => getThemePreference());

  return (
    <div className="rounded-lg border border-border bg-surface-1">
      <CardHeader
        icon={<Monitor size={16} strokeWidth={1.5} />}
        title="Appearance"
        value="Colors follow this device, or stay light or dark."
      />
      <div className="border-t border-border p-4">
        <Segmented
          label="Theme"
          value={theme}
          options={THEME_OPTIONS}
          onChange={(next) => {
            setTheme(next);
            setThemePreference(next);
          }}
        />
      </div>
    </div>
  );
}

function EmailCard() {
  const { data: user } = useCurrentUser();
  const change = useChangeEmail();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const reset = () => {
    setEmail('');
    setPassword('');
    setOpen(false);
  };

  return (
    <div className="rounded-lg border border-border bg-surface-1">
      <CardHeader
        icon={<Mail size={16} strokeWidth={1.5} />}
        title="Email"
        value={user?.email}
        action={
          <Button variant={open ? 'secondary' : 'ghost'} onClick={() => (open ? reset() : setOpen(true))}>
            {open ? 'Cancel' : 'Change'}
          </Button>
        }
      />

      <Collapse open={open}>
        <form
          className="flex flex-col gap-4 border-t border-border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate(
              { current_password: password, new_email: email.trim() },
              {
                onSuccess: () => {
                  reset();
                  toast.show('Email updated', { tone: 'success' });
                },
                onError: (err) =>
                  toast.error(
                    err instanceof ApiError ? err.message : 'Could not change email',
                  ),
              },
            );
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="New email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <Field
              label="Current password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              hint="Confirms it is you, not a borrowed session."
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" variant="primary" loading={change.isPending}>
              Update email
            </Button>
          </div>
        </form>
      </Collapse>
    </div>
  );
}

const MIN_PASSWORD = 12;

function PasswordCard() {
  const change = useChangePassword();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setOpen(false);
  };

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && confirm !== next;

  return (
    <div className="rounded-lg border border-border bg-surface-1">
      <CardHeader
        icon={<KeyRound size={16} strokeWidth={1.5} />}
        title="Password"
        value="Changing it signs out every device, including this one."
        action={
          <Button variant={open ? 'secondary' : 'ghost'} onClick={() => (open ? reset() : setOpen(true))}>
            {open ? 'Cancel' : 'Change'}
          </Button>
        }
      />

      <Collapse open={open}>
        <form
          className="flex flex-col gap-4 border-t border-border p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (next.length < MIN_PASSWORD || next !== confirm) return;
            change.mutate(
              { current_password: current, new_password: next },
              {
                onSuccess: () => {
                  reset();
                  toast.show('Password changed. Sign in again', { tone: 'success' });
                },
                onError: (err) =>
                  toast.error(
                    err instanceof ApiError ? err.message : 'Could not change password',
                  ),
              },
            );
          }}
        >
          <Field
            label="Current password"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="New password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              autoComplete="new-password"
              hint={`At least ${MIN_PASSWORD} characters.`}
              error={tooShort ? `Use at least ${MIN_PASSWORD} characters` : undefined}
            />
            <Field
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              error={mismatch ? 'Passwords do not match' : undefined}
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={change.isPending}
              disabled={!current || next.length < MIN_PASSWORD || next !== confirm}
            >
              Change password
            </Button>
          </div>
        </form>
      </Collapse>
    </div>
  );
}
